// React-SVG-Bausteine für die interaktiven Vorschauen (Editor-Look).
// Reine Funktionen, keine externe Library. Farben aus den CSS-Tokens (HSL).
import React from "react";

export const C = {
  wall: "hsl(36 25% 94%)",
  wallStroke: "hsl(30 14% 78%)",
  object: "hsl(188 72% 28% / 0.10)",
  objectStroke: "hsl(188 72% 28%)",
  drill: "hsl(24 92% 55%)",
  center: "hsl(215 12% 55%)",
  dim: "hsl(215 12% 42%)",
  floor: "hsl(30 14% 64%)",
  text: "hsl(222 22% 12%)",
  primary: "hsl(188 72% 28%)",
  accent: "hsl(24 92% 55%)",
  warn: "hsl(0 72% 52%)",
  guide: "hsl(215 12% 70%)",
  blue: "hsl(205 78% 40%)",
};

const FONT = "Inter, sans-serif";

// Maßangabe als kleine Pille (weiß mit farbigem Rand), in der viewBox geclamped.
export function pill(key, x, y, text, color = C.dim, anchor = "middle", vbW = 880) {
  const w = String(text).length * 7.2 + 16;
  const h = 22;
  let bx = x - (anchor === "end" ? w : anchor === "start" ? 0 : w / 2);
  bx = Math.max(6, Math.min(bx, vbW - 6 - w));
  return (
    <g key={key}>
      <rect x={bx} y={y - h / 2} width={w} height={h} rx={6} fill="#fff" stroke={color} strokeWidth={1.2} />
      <text
        x={bx + w / 2}
        y={y + 0.5}
        fill={color}
        fontSize={13}
        fontWeight={600}
        textAnchor="middle"
        dominantBaseline="middle"
        fontFamily={FONT}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {text}
      </text>
    </g>
  );
}

// Horizontale Maßlinie mit Endkappen und optionaler Pille.
export function hDim(key, x1, x2, y, label, color = C.dim, vbW = 880) {
  return (
    <g key={key}>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth={1.4} />
      <line x1={x1} y1={y - 5} x2={x1} y2={y + 5} stroke={color} strokeWidth={1.4} />
      <line x1={x2} y1={y - 5} x2={x2} y2={y + 5} stroke={color} strokeWidth={1.4} />
      {label != null ? pill(key + "p", (x1 + x2) / 2, y, label, color, "middle", vbW) : null}
    </g>
  );
}

// Vertikale Maßlinie.
export function vDim(key, y1, y2, x, label, color = C.dim, vbW = 880) {
  return (
    <g key={key}>
      <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth={1.4} />
      <line x1={x - 5} y1={y1} x2={x + 5} y2={y1} stroke={color} strokeWidth={1.4} />
      <line x1={x - 5} y1={y2} x2={x + 5} y2={y2} stroke={color} strokeWidth={1.4} />
      {label != null ? pill(key + "p", x, (y1 + y2) / 2, label, color, "middle", vbW) : null}
    </g>
  );
}

// Anfasser zum Ziehen (Kreis mit Doppelstrich). hit = unsichtbare Trefferfläche.
export function handle(key, cx, cy, cursor, onDown, orient = "h") {
  const hit = 34;
  const r = 12;
  const lines =
    orient === "v"
      ? [
          [cx - 4, cy - 5, cx - 4, cy + 5],
          [cx + 4, cy - 5, cx + 4, cy + 5],
        ]
      : [
          [cx - 5, cy - 4, cx + 5, cy - 4],
          [cx - 5, cy + 4, cx + 5, cy + 4],
        ];
  return (
    <g key={key} style={{ cursor }} onPointerDown={onDown}>
      <rect x={cx - hit / 2} y={cy - hit / 2} width={hit} height={hit} fill="transparent" />
      <circle cx={cx} cy={cy} r={r} fill="#fff" stroke={C.primary} strokeWidth={2.5} />
      {lines.map((l, i) => (
        <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]} stroke={C.primary} strokeWidth={2} strokeLinecap="round" />
      ))}
    </g>
  );
}

// Bohrpunkt als Fadenkreuz. Mit onDown wird er zum Drag-Griff.
export function cross(key, cx, cy, onDown) {
  const r = 10;
  return (
    <g key={key} style={{ cursor: onDown ? "ns-resize" : "default" }} onPointerDown={onDown}>
      {onDown ? <rect x={cx - 22} y={cy - 22} width={44} height={44} fill="transparent" /> : null}
      <circle cx={cx} cy={cy} r={r} fill="#fff" stroke={C.accent} strokeWidth={2.5} />
      <line x1={cx - r} y1={cy} x2={cx + r} y2={cy} stroke={C.accent} strokeWidth={1.6} />
      <line x1={cx} y1={cy - r} x2={cx} y2={cy + r} stroke={C.accent} strokeWidth={1.6} />
    </g>
  );
}

// SVG-Wrapper mit fester viewBox, touch-action none (für sauberes Drag auf Mobile).
export function Svg({ vbW, vbH, children, ariaLabel }) {
  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      width="100%"
      role="img"
      aria-label={ariaLabel || "Interaktive Vorschau der Bohrpunkte"}
      style={{
        width: "100%",
        height: "auto",
        display: "block",
        touchAction: "none",
        userSelect: "none",
        WebkitUserSelect: "none",
      }}
    >
      {children}
    </svg>
  );
}

// Lineal-Beschriftung (kleine Striche + Zahlen). Liefert ein Array von Elementen.
export function fmtTick(n) {
  const r = Math.round(n * 10) / 10;
  return (Number.isInteger(r) ? r.toString() : r.toFixed(1)).replace(".", ",");
}
